import React, { Component, createRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import { replace, push } from 'redux-first-router';
import Link from 'redux-first-router-link';

import { retrieveProductCache } from '../../actions/checkout';
import { searchInput, goToPage, resetFilter } from '../../actions/environment';
import { purifier } from '../../utils/dom';
import { translate, prefix } from '../../utils/locale';
import { splitNewLine } from '../../utils/string';

import { unique, intersect } from '../../utils/array';
import s from './SearchForm.scss';

class SearchForm extends Component {
	constructor(props) {
		super(props);

		this.debounceTimer = false;
		this.inputElement = createRef();
	}

	searchHandler = (e) => {
		e.preventDefault();
		this.props.toggleSearchSuggestionsHandler(false);
	};

	triggerSearchEvent = (value) => {
		if (typeof window !== 'undefined') {
			if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
			if (value.length > 3) {
				window.dataLayer.push({ values: null });
				window.dataLayer.push({
					event: 'Search',
					values: {
						language: this.props.setup.language,
						search_string: value.toLowerCase(),
					},
				});

				window.dataLayer.push({ pinterest: null });
				window.dataLayer.push({
					event: 'search_pinterest',
					pinterest: {
						search_query: value.toLowerCase(),
					},
				});
			}
		}
	};

	onSearchInputChange = (e) => {
		e.preventDefault();
		const $input = e.currentTarget;
		const { value = '' } = $input;
		const url = prefix(this.props.setup, `search${value.length > 0 ? '?q=' : ''}${value.toLowerCase().replace(/\s/g, '-')}`);
		replace(url);
		if (this.props.location.type !== 'SEARCH') this.props.resetFilter();
		this.props.searchInput(value);
		this.props.toggleSearchSuggestionsHandler(true);
		clearTimeout(this.debounceTimer);
		this.debounceTimer = setTimeout(() => {
			this.triggerSearchEvent(value);
			this.props.resetFilter();
			push(url);
		}, 800);
	};

	addMatchHandler = (e) => {
		const value = e.currentTarget.textContent;
		this.props.resetFilter();
		this.props.searchInput(value);
		this.props.toggleSearchSuggestionsHandler(false);
		this.triggerSearchEvent(value);
	}

	clearTextHandler = (e) => {
		e.preventDefault();
		const { searchInputText = '' } = this.props;
		if (searchInputText === '') {
			this.props.closeSearch();
		} else {
			this.props.searchInput('');
			this.inputElement.current.focus();
		}
	};

	fakeTab = (e) => {
		if (e.key === 'ArrowDown' && e.currentTarget.nextSibling) {
			e.preventDefault();
			e.currentTarget.nextSibling.focus();
		}
		if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (e.currentTarget.previousSibling) e.currentTarget.previousSibling.focus();
			else e.currentTarget.parentNode.previousSibling.previousSibling.focus();
		}
	};

	fakeTabInput = (e) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (this.props.showSearchSuggestions) {
				e.currentTarget.nextSibling.nextSibling.children[0].focus();
			} else {
				this.props.toggleSearchSuggestionsHandler(true);
			}
		}
	};

	render() {
		const { setup, searchInputText = '', toggleSearchSuggestionsHandler, showSearch, showSearchSuggestions, productCache } = this.props;
		if (!productCache) return null;
		if (!productCache[setup.market] || !productCache[setup.market][setup.pricelist]) {
			this.props.retrieveProductCache();
			return null;
		}

		const filtering = productCache[setup.market][setup.pricelist];
		const searchTerms = filtering.searchTerms.en;
		const searchInputParts = searchInputText.split(' ');
		const searchInputPartsRegExp = new RegExp(`(${searchInputParts.join('|')})`, 'gi');
		let matchingSearchTerms = splitNewLine(translate(setup, 'searchSuggestions'));
		let productList = [];

		if (searchInputText !== '' && searchInputText.length > 2) {
			searchInputParts.forEach((wordPart, i) => {
				if (wordPart === '') return;
				let currentSearchMatch = [];

				Object.keys(searchTerms).forEach((term) => {
					const wordPartIndex = term.toLowerCase().indexOf(wordPart.toLowerCase());
					let specificity = 1;

					if (wordPartIndex !== -1) {
						specificity = 1 / searchTerms[term].length; // More products per key = less specific
						specificity = specificity * 100 * (wordPartIndex === 0 ? 10 : 1);

						currentSearchMatch.push({ term, specificity, products: searchTerms[term] });
					}
				});

				if (currentSearchMatch.length === 0) return;

				currentSearchMatch.sort((a, b) => a.specificity - b.specificity);
				const currentSearchTerms = currentSearchMatch.map((m) => m.term);
				matchingSearchTerms = i === 0 ? [...currentSearchTerms] : intersect(matchingSearchTerms, currentSearchTerms);
				currentSearchMatch = currentSearchMatch.reduce((acc, product) => [...acc, ...product.products], []);
				currentSearchMatch = unique(currentSearchMatch);

				productList = i === 0 ? [...currentSearchMatch] : intersect(productList, currentSearchMatch);
			});
		}

		productList = productList.sort((a, b) => {
			if (!filtering.products[a] || !filtering.products[b]) return -1;
			return filtering.products[a].added < filtering.products[b].added ? 1 : -1;
		});

		return (
			<form className={[s.searchForm, (showSearch ? s.searchForm__showing : null)].join(' ')} onSubmit={this.searchHandler}>
				<div className={s.searchForm_input}>
					<input id="search" ref={this.inputElement} className={s.searchForm_input_field} type="search" value={searchInputText} onKeyDown={this.fakeTabInput} onChange={this.onSearchInputChange} onFocus={toggleSearchSuggestionsHandler} placeholder={translate(setup, 'searchPlaceholder')} autoComplete="off" />
					{
						searchInputText.length > 2 && (
							<a className={s.searchForm_input_clear} href="#clearText" onClick={this.clearTextHandler}>
								<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M0 0.706483L0.707 0L13.435 12.7187L12.728 13.4252L0 0.706483Z" fill="black" />
									<path d="M0.707 13.4252L0 12.7187L12.728 0L13.435 0.706483L0.707 13.4252Z" fill="black" />
								</svg>
							</a>
						)
					}
					{
						matchingSearchTerms.length > 0 && (
							<div className={[s.searchForm_input_matches, showSearchSuggestions ? s.searchForm_input_matches__showing : null].join(' ')}>
								{
									matchingSearchTerms.slice(0, 5).map((m) => {
										const matchedParts = m.replace(searchInputPartsRegExp, (m1) => `<strong>${m1}</strong>`);
										return <Link key={m} className={s.searchForm_input_match} to={prefix(setup, `search?q=${m.replace(/\s/g, '-').toLowerCase()}`)} onClick={this.addMatchHandler} onKeyDown={this.fakeTab} dangerouslySetInnerHTML={{ __html: purifier(matchedParts) }} aria-label={m} />;
									})
								}
							</div>
						)
					}
				</div>
				<button className={s.searchForm_submit} type="submit" aria-label="Search" />
			</form>
		);
	}
}

const mapState = ({ environment, productCache, filtering, location }, props) => ({
	setup: environment.setup,
	searchInputText: environment.searchInputText,
	productCache,
	filtering,
	location,
	...props,
});

export default hot(module)(connect(mapState, { searchInput, goToPage, resetFilter, retrieveProductCache })(SearchForm));
